import { useAtomValue, useSetAtom } from 'infrastructure/state/jotai.wrapper'
import {
  currentMappingSetAtom,
  lessonAssociationsAtom,
  treeStructureResultAtom,
} from './atoms'
import { useCallback, useState } from 'react'
import { googleUserAtom } from 'infrastructure/googleDrive'
import {
  SpreadsheetImportData,
  LessonAssociation,
} from 'domains/scope-and-sequence/types'
import { isAuthError } from 'components/tree/SyncStandards/utility'
import { convertSpreadsheetToLessonCentric } from './convertFromApi'
import { getCrosswalkById } from 'domains/scope-and-sequence/api'

function convertToAssociations(
  data: SpreadsheetImportData,
): Record<string, LessonAssociation> {
  const associations: Record<string, LessonAssociation> = {}

  data.lessons.forEach((lesson) => {
    const key = `${lesson.treeId}:${lesson.versionId}:${lesson.nodeId}`
    const existing = associations[key]

    // If we already have an association with a targetUnit, don't overwrite with null
    if (
      existing &&
      existing.targetUnit !== null &&
      lesson.targetUnit === null
    ) {
      return
    }

    associations[key] = {
      lessonKey: key,
      targetUnit: lesson.targetUnit,
      targetSection: lesson.targetSection,
      targetLessonNumber: lesson.targetLessonNumber,
    }
  })

  return associations
}

export function useSpreadsheetImport() {
  const guser = useAtomValue(googleUserAtom)
  const resetGoogleAuth = useSetAtom(googleUserAtom)
  const setTreeStructureResult = useSetAtom(treeStructureResultAtom)
  const setCurrentMappingSet = useSetAtom(currentMappingSetAtom)
  const setLessonAssociations = useSetAtom(lessonAssociationsAtom)

  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [importData, setImportData] = useState<SpreadsheetImportData | null>(
    null,
  )

  const fetchAndImport = useCallback(
    async (
      sheetId: string,
      fetchSpreadsheetData: (
        accessToken: string,
        spreadsheetId: string,
      ) => Promise<SpreadsheetImportData>,
    ): Promise<boolean> => {
      if (!guser.credential?.accessToken) {
        setError('Not authenticated with Google')
        return false
      }

      setLoading(true)
      setError(null)

      try {
        const data = await fetchSpreadsheetData(
          guser.credential.accessToken,
          sheetId,
        )
        setImportData(data)

        // Convert and store in atom
        const converted = convertSpreadsheetToLessonCentric(data)
        setTreeStructureResult(converted)

        // Set associations
        const associations = convertToAssociations(data)
        setLessonAssociations(associations)

        // Set crosswalk mapping set if found in metadata
        if (data.crosswalkMappingSetId) {
          const { result: crosswalk } = await getCrosswalkById(
            data.crosswalkMappingSetId,
          )
          setCurrentMappingSet(crosswalk)
        } else {
          setCurrentMappingSet(undefined)
        }

        return true
      } catch (err) {
        if (isAuthError(err)) {
          resetGoogleAuth()
        }
        setError(
          err instanceof Error ? err.message : 'Failed to fetch spreadsheet',
        )
        return false
      } finally {
        setLoading(false)
      }
    },
    [
      guser,
      setTreeStructureResult,
      setCurrentMappingSet,
      setLessonAssociations,
      resetGoogleAuth,
    ],
  )

  const clearError = useCallback(() => setError(null), [])
  const clearImport = useCallback(() => setImportData(null), [])

  return {
    loading,
    error,
    importData,
    fetchAndImport,
    clearError,
    clearImport,
    setError,
  }
}
